import { useQuery } from '@apollo/client';
import {
  Arrange,
  Button,
  Container,
  Distribute,
  Link,
  Spacer,
  Text,
  Tooltip,
} from '@loomhq/lens';
import classNames from 'classnames';
import pluralize from 'pluralize';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { devToolsEnabled } from 'constants/devtools';
import { isDev } from 'constants/environment';
import {
  ORG_ROLE_ADMIN,
  ORG_ROLE_CREATOR_LITE,
} from 'constants/organizationRoles';
import {
  ADMIN_PAGE,
  CHROME_EXT_WEBSTORE_URL,
  CUSTOMERS_PAGE,
  DESKTOP,
  MOBILE_ANDROID_APP,
  MOBILE_IOS_APP,
  MOBILE_WEB,
  NEW_LOOM_URI,
  PROFILE_PAGE,
  SUPPORT_URI,
  USE_CASES,
} from 'constants/routes';
import { ADMIN } from 'constants/userRoles';
import {
  WORKSPACE_PLAN_BUSINESS,
  WORKSPACE_PLAN_STARTER_FREE,
} from 'constants/workspacePlans';
import GetUserProfileVideoCount from 'creators/GetUserProfileVideoCount.graphql';
import { logoutUserCreator } from 'creators/user';
import { selectCurrentUser } from 'selectors/user';
import { getUserAvatarThumb } from 'utilities/avatar';
import { isAndroid, isIOS, isMobile } from 'utilities/device';

import useClickOutside from 'hooks/useClickOutside';
import useDestination from 'hooks/useDestination';
import {
  useGetSelectedWorkspace,
  useGetUserRoleForSelectedWorkspace,
} from 'hooks/workspace';

import UpgradePrompt from './upgrade-prompt';
import DarkModeToggle from '../dark-mode-toggle/dark-mode-toggle';
import { DARK_MODE_TOGGLE } from '../../../../shared/constants/featureFlag';

import { useDevTools } from 'components/devtools/devtools-store';
import FeatureFlaggedComponent from 'components/feature-flag-wrapper';
import UserAvatar from 'components/user-avatar';

import styles from './styles.module.less';

const ACCOUNT_ITEM = {
  label: 'Account & preferences',
  href: PROFILE_PAGE,
};

const WHATS_NEW_ITEM = {
  label: 'What’s new',
  href: NEW_LOOM_URI,
  newTab: true,
};

const USE_CASES_ITEM = {
  label: 'Ways to use Loom',
  href: USE_CASES,
  newTab: true,
};

const CUSTOMERS_ITEM = {
  label: 'Customers',
  href: CUSTOMERS_PAGE,
  newTab: true,
};

const HELP_ITEM = {
  label: 'Help center',
  href: SUPPORT_URI,
  newTab: true,
};

const LOG_OUT_ITEM = {
  label: 'Log Out',
};

const ADMIN_ITEM = {
  label: 'Loommate portal',
  href: ADMIN_PAGE,
};

const SECONDARY_MENU_ITEMS = [
  {
    label: 'Install Chrome extension',
    href: CHROME_EXT_WEBSTORE_URL,
  },
  {
    label: 'Download desktop app',
    href: DESKTOP,
  },
];

export const ProfileBubb = ({ onOpen }) => {
  const user = useSelector(selectCurrentUser);
  const showDestination = useDestination();

  const onClick = () => {
    document.body.classList.add('noscroll');
    onOpen();
  };

  return (
    <>
      {showDestination === false && (
        <Tooltip content={ACCOUNT_ITEM.label} placement="bottomRight">
          <UserAvatar
            avatarSrc={getUserAvatarThumb(user.avatars)}
            name={user.first_name}
            onClick={onClick}
          />
        </Tooltip>
      )}
      {showDestination === true && (
        <div id="intercom-destination-avatar">
          <Tooltip
            content="Profile and personal settings"
            placement="bottomRight"
          >
            <UserAvatar
              avatarSrc={getUserAvatarThumb(user.avatars)}
              name={user.first_name}
              onClick={onClick}
            />
          </Tooltip>
        </div>
      )}
    </>
  );
};

export const BubbleMenu = ({ onExit }) => {
  const openDevToolsModal = useDevTools(state => state.openDevToolsModal);
  const user = useSelector(state => {
    const {
      avatars,
      id,
      first_name: firstName,
      last_name: lastName,
      email,
      role,
    } = state.user.current;
    const name = `${firstName} ${lastName}`.trim() || email;

    return { avatars, name, email, id, isLoomAdmin: role === ADMIN };
  });
  const selectedWorkspace = useGetSelectedWorkspace();
  const userRole = useGetUserRoleForSelectedWorkspace();
  const displayUpgradePrompt =
    (selectedWorkspace?.type === WORKSPACE_PLAN_STARTER_FREE &&
      [ORG_ROLE_ADMIN, ORG_ROLE_CREATOR_LITE].includes(userRole)) ||
    (selectedWorkspace?.type === WORKSPACE_PLAN_BUSINESS &&
      userRole === ORG_ROLE_CREATOR_LITE);
  const ref = useClickOutside(onExit);

  const { data } = useQuery(GetUserProfileVideoCount, {
    variables: {
      userId: user?.id,
    },
  });

  const showDestination = useDestination();

  const numPostedVideos = data?.result?.user?.profile?.profileVideoCount;
  const postedVideosText =
    numPostedVideos > 0
      ? `${numPostedVideos} ${pluralize('video', numPostedVideos)} posted to ${
          selectedWorkspace.name
        }`
      : `Zero videos posted to ${selectedWorkspace.name}`;

  const downloadMobile = { label: 'Download mobile app' };

  if (!isMobile) {
    downloadMobile['href'] = MOBILE_WEB;
  } else if (isIOS) {
    downloadMobile['href'] = MOBILE_IOS_APP;
  } else if (isAndroid) {
    downloadMobile['href'] = MOBILE_ANDROID_APP;
  }

  const dispatch = useDispatch();

  useEffect(() => {
    const onLogOutClick = e => {
      e.preventDefault();
      dispatch(logoutUserCreator());
    };

    LOG_OUT_ITEM.onClick = onLogOutClick;
  }, [dispatch]);

  const mainMenuItems = [
    WHATS_NEW_ITEM,
    USE_CASES_ITEM,
    CUSTOMERS_ITEM,
    HELP_ITEM,
  ];

  return (
    <>
      {showDestination === false && (
        <div className={styles.bubbleMenu} ref={ref}>
          <Distribute gap={1} direction="column">
            <Text fontWeight="bold">{user.name}</Text>
            <Text color="bodyDimmed">{user.email}</Text>
          </Distribute>
          <Distribute gap="small" direction="column" className={styles.links}>
            <BubbMenuItem key={ACCOUNT_ITEM.href} item={ACCOUNT_ITEM} />
          </Distribute>
          {displayUpgradePrompt && (
            <Distribute gap="small" direction="column" className={styles.links}>
              <UpgradePrompt />
            </Distribute>
          )}
          <Distribute gap={1} direction="column" className={styles.links}>
            {mainMenuItems.map(item => (
              <BubbMenuItem key={item.href} item={item} />
            ))}
            <a
              onClick={LOG_OUT_ITEM.onClick}
              title="signout"
              href="#"
              className={styles.menuItem}
            >
              Sign out
            </a>
          </Distribute>
          {(user.isLoomAdmin || isDev) && (
            <Distribute
              gap={1}
              direction="column"
              className={classNames(styles.links)}
            >
              <BubbMenuItem key={ADMIN_ITEM.href} item={ADMIN_ITEM} />
              <Spacer left={1}>
                <Text color="bodyDimmed">User ID: {user.id}</Text>
                <Link
                  href={`/admin/workspaces/workspace-lookup/${selectedWorkspace.id}`}
                  target="_blank"
                >
                  <Text color="bodyDimmed">
                    Workspace ID: {selectedWorkspace.id} ↗
                  </Text>
                </Link>
              </Spacer>
            </Distribute>
          )}
          {devToolsEnabled && (
            <div className={styles.links}>
              <button
                className={classNames(styles.devToolsButton, styles.menuItem)}
                onClick={() => openDevToolsModal()}
              >
                <Text>Open devtools</Text>
              </button>
            </div>
          )}
          <FeatureFlaggedComponent
            flag={DARK_MODE_TOGGLE}
            activationValues={[true]}
          >
            <DarkModeToggle />
          </FeatureFlaggedComponent>
          <Distribute
            gap={1}
            direction="column"
            className={classNames(styles.links, styles.subLinks)}
          >
            {(!isMobile || isIOS || isAndroid) && (
              <BubbMenuItem key={downloadMobile.href} item={downloadMobile} />
            )}

            {SECONDARY_MENU_ITEMS.map(item => (
              <BubbMenuItem key={item.href} item={item} />
            ))}
          </Distribute>
        </div>
      )}
      {showDestination === true && (
        <div className={styles.bubbleMenu} ref={ref}>
          <Arrange autoFlow="row" gap="small" justifyContent="center">
            <Arrange justifyContent="center">
              <UserAvatar
                avatarSrc={getUserAvatarThumb(user.avatars)}
                name={user.name}
                size={5}
              />
            </Arrange>
            <Container minWidth={0}>
              <Text alignment="center" fontWeight="medium" hasEllipsis>
                {user.name}
              </Text>
              <Text alignment="center" color="bodyDimmed">
                {postedVideosText}
              </Text>
            </Container>
            <Button htmlTag="a" href="/profile" variant="primary">
              View or edit profile
            </Button>
          </Arrange>
          <Distribute gap="small" direction="column" className={styles.links}>
            <BubbMenuItem key={ACCOUNT_ITEM.href} item={ACCOUNT_ITEM} />
          </Distribute>
          {displayUpgradePrompt && (
            <Distribute gap="small" direction="column" className={styles.links}>
              <UpgradePrompt />
            </Distribute>
          )}
          <Distribute gap="small" direction="column" className={styles.links}>
            {SECONDARY_MENU_ITEMS.map(item => (
              <BubbMenuItem key={item.href} item={item} />
            ))}
            {(!isMobile || isIOS || isAndroid) && (
              <BubbMenuItem key={downloadMobile.href} item={downloadMobile} />
            )}
          </Distribute>
          <Distribute gap="small" direction="column" className={styles.links}>
            <a
              onClick={LOG_OUT_ITEM.onClick}
              title="signout"
              href="#"
              className={styles.menuItem}
            >
              Sign out
            </a>
          </Distribute>
          {(user.isLoomAdmin || isDev) && (
            <Distribute
              gap={1}
              direction="column"
              className={classNames(styles.links)}
            >
              <BubbMenuItem key={ADMIN_ITEM.href} item={ADMIN_ITEM} />
              <Spacer left={1}>
                <Text color="bodyDimmed">Email: {user.email}</Text>
                <Text color="bodyDimmed">User ID: {user.id}</Text>
                <Link
                  href={`/admin/workspaces/workspace-lookup/${selectedWorkspace.id}`}
                  target="_blank"
                >
                  <Text color="bodyDimmed">
                    Workspace ID: {selectedWorkspace.id} ↗
                  </Text>
                </Link>
              </Spacer>
            </Distribute>
          )}
          {devToolsEnabled && (
            <div className={styles.links}>
              <button
                className={classNames(styles.devToolsButton, styles.menuItem)}
                onClick={() => openDevToolsModal()}
              >
                <Text>Open devtools</Text>
              </button>
            </div>
          )}
          <FeatureFlaggedComponent
            flag={DARK_MODE_TOGGLE}
            activationValues={[true]}
          >
            <DarkModeToggle />
          </FeatureFlaggedComponent>
        </div>
      )}
    </>
  );
};

const BubbMenuItem = ({ item }) => (
  // TODO: Add analytics calls here
  // eslint-disable-next-line react/jsx-no-target-blank
  <a
    href={item.href}
    title={item.label}
    className={styles.menuItem}
    target={item.newTab ? '_blank' : '_self'}
    rel={item.newTab ? 'noopener noreferrer' : null}
  >
    {item.label}
  </a>
);
